/**
 * Admin: asesoramientos expertos Academia (CRUD).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export type AdvisoryStatus = 'draft' | 'published' | 'archived';

export interface AcademyExpertAdvisoryAdminRow {
  id: string;
  slug: string;
  modality: string;
  title: string;
  tagline: string | null;
  description: string | null;
  collaborator_user_id: string | null;
  collaborator_display_name: string;
  cover_image_url: string;
  whatsapp_phone: string;
  price_eur: number;
  duration_minutes: number;
  topics: unknown;
  modality_techniques: string[] | null;
  youtube_url: string | null;
  tiktok_url: string | null;
  instagram_url: string | null;
  facebook_url: string | null;
  sort_order: number;
  status: AdvisoryStatus;
  created_at: string;
  updated_at: string;
}

export interface AdvisoryUpsertInput {
  id?: string | null;
  slug: string;
  modality: string;
  title: string;
  tagline?: string;
  description?: string;
  collaboratorUserId?: string | null;
  collaboratorDisplayName: string;
  coverImageUrl: string;
  whatsappPhone: string;
  priceEur: number;
  durationMinutes: number;
  topics: string[];
  modalityTechniques: string[];
  youtubeUrl?: string;
  tiktokUrl?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  sortOrder: number;
  status: AdvisoryStatus;
}

const QK = ['admin', 'academy', 'expert-advisories'] as const;

export function useAdminAcademyExpertAdvisories(status: string, search: string) {
  return useQuery({
    queryKey: [...QK, status, search],
    queryFn: async (): Promise<AcademyExpertAdvisoryAdminRow[]> => {
      const params: Record<string, unknown> = { p_limit: 100 };
      if (status && status !== 'all') params.p_status = status;
      if (search.trim()) params.p_search = search.trim();
      const { data, error } = await dbClient.rpc(
        'admin_academy_expert_advisories_list',
        params,
      );
      if (error) throw new Error(error.message);
      return normalizeRpcResponse(data) as AcademyExpertAdvisoryAdminRow[];
    },
    staleTime: 20_000,
  });
}

export function useUpsertAcademyExpertAdvisory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdvisoryUpsertInput) => {
      const { data, error } = await dbClient.rpc('admin_academy_expert_advisories_upsert', {
        p_id: input.id ?? null,
        p_slug: input.slug,
        p_modality: input.modality,
        p_title: input.title,
        p_tagline: input.tagline ?? null,
        p_description: input.description ?? null,
        p_collaborator_user_id: input.collaboratorUserId || null,
        p_collaborator_display_name: input.collaboratorDisplayName,
        p_cover_image_url: input.coverImageUrl,
        p_whatsapp_phone: input.whatsappPhone,
        p_price_eur: input.priceEur,
        p_duration_minutes: input.durationMinutes,
        p_topics: input.topics,
        p_modality_techniques: input.modalityTechniques,
        p_youtube_url: input.youtubeUrl ?? null,
        p_tiktok_url: input.tiktokUrl ?? null,
        p_instagram_url: input.instagramUrl ?? null,
        p_facebook_url: input.facebookUrl ?? null,
        p_sort_order: input.sortOrder,
        p_status: input.status,
      });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
    },
  });
}

export function useSetAcademyExpertAdvisoryStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: AdvisoryStatus }) => {
      const { error } = await dbClient.rpc('admin_academy_expert_advisories_set_status', {
        p_id: id,
        p_status: status,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
    },
  });
}
